/*
 * User API Routes
 *
 * Features:
 * - List users and manage per-user account/contact links
 * - Return current session user
 * - Report and clear the current user's stored CRM API token hash
 *
 * Changes:
 * - 2026-09-09: Exposes uncached user access levels and audited administrator-only level updates.
 * - 2026-05-20: Added /users/me/pat status and clear routes for PAT revocation
 */
import { z } from "zod";
import { DataRoutes } from "./types";

const usersRoutes: DataRoutes = {

  // Get all users
  'get /api/data/users': {
    query: `SELECT id, name, email, AccessLevel AS accessLevel FROM [Users]`,
    cache: false,
  },

  // SQL verifies current User Admin membership and records actor/old/new levels atomically.
  'put /api/data/users/:userId/access-level': {
    schema: z.object({
      userId: z.number().int().positive(),
      accessLevel: z.number().int().min(1).max(4),
    }),
    allowCrossUserAccess: true,
    queryOne: `EXEC dbo.SetUserAccessLevel @UserId = @userId, @AccessLevel = @accessLevel`,
  },

  // Add a contact to user's contact list by email
  'post /api/data/users/:userId/contacts': {
    schema: z.object({
      id: z.number().int().default(-1)
    }),
    query: `EXEC [dbo].[AddToUserContacts] @userId, @id`
  },

  // Remove a contact from user's contact list
  'delete /api/data/users/:userId/contacts/:id': {
    schema: z.object({
      userId: z.number().int(),
      id: z.number().int()
    }),
    query: `DELETE FROM [UserContacts] WHERE [UserId] = @userId AND ContactId = @id`
  },

  // Add an account to user's account list by account id
  'post /api/data/users/:userId/accounts': {
    schema: z.object({
      id: z.number().int()
    }),
    query: `
        IF NOT EXISTS (SELECT 1 FROM [UserAccounts] WHERE [UserId] = @userId AND AccountId = @id)
        BEGIN
            INSERT INTO [UserAccounts] (UserId, AccountId) VALUES (@userId, @id)
        END`
  },

  // Remove a account from user's account list
  'delete /api/data/users/:userId/accounts/:id': {
    schema: z.object({
      userId: z.number().int(),
      id: z.number().int()
    }),
    query: `DELETE FROM [UserAccounts] WHERE [UserId] = @userId AND AccountId = @id`
  },

  'get /api/data/who': {
    query: `EXEC [dbo].[GetSessionUser]`,
    cache: false,
  },

  'get /api/data/users/me/pat': {
    queryOne: `
      SELECT CAST(CASE WHEN [Pat] IS NULL THEN 0 ELSE 1 END AS bit) AS [hasPat]
      FROM [Users]
      WHERE [Email] = CONVERT(NVARCHAR(255), SESSION_CONTEXT(N'user'))
    `
  },

  'delete /api/data/users/me/pat': {
    queryOne: `
      UPDATE [Users]
      SET [Pat] = NULL,
          [UpdatedAt] = sysdatetime()
      OUTPUT CAST(0 AS bit) AS [hasPat]
      WHERE [Email] = CONVERT(NVARCHAR(255), SESSION_CONTEXT(N'user'))
    `
  }
}

export default usersRoutes;
